/* GENERATED FILE — DO NOT EDIT.
 *
 * A workout's name is at most 28 characters (W84 page 1, item 5 — his 2026-09-26 word),
 * emitted from the definition the app compiles:
 *   HybridTrainer/Services/WorkoutNameCap.swift
 *
 * Regenerate:  KATA_EMIT_WEB_TOKENS=1 ./tools/engine-tests/run.sh
 * The harness gate `w84-1-the-workout-page` regenerates this in memory on every run and fails if it differs.
 *
 * A character is a Unicode code point (`[...name]`) — what Postgres' `char_length` and Swift's `unicodeScalars`
 * count; `.length` (UTF-16 units) would disagree with the phone on an emoji.
 */

export const WORKOUT_NAME_MAX = 28;
export const WORKOUT_NAME_REFUSAL = "Names stop at 28 characters so they fit on one line";

export function workoutNameLength(name: string): number {
  return [...name].length;
}

/** The name with everything past the cap refused. */
export function clampWorkoutName(name: string): string {
  const points = [...name];
  return points.length <= WORKOUT_NAME_MAX ? name : points.slice(0, WORKOUT_NAME_MAX).join("");
}

/** A name with a suffix that must still fit — a duplicate's `X (copy N)`: the suffix whole, the base cut and
 *  marked `…` (trailing spaces / an ellipsis already there dropped first), so the result holds the cap. */
export function fitWorkoutName(base: string, suffix: string): string {
  const b = [...base];
  const s = [...suffix];
  if (b.length + s.length <= WORKOUT_NAME_MAX) return base + suffix;
  const room = Math.max(WORKOUT_NAME_MAX - s.length - 1, 0);
  const head = b.slice(0, room);
  while (head.length > 0 && TRAILING_TRIM.has(head[head.length - 1]!)) head.pop();
  return `${head.join("")}\u2026${suffix}`;
}

const TRAILING_TRIM: ReadonlySet<string> = new Set([" ", "\t", "\u2026"]);

/** Swift's own answers on the suffix probes — `workoutNameCap.test.ts` holds `fitWorkoutName` to them. */
export const SWIFT_SUFFIX_PROBES: readonly { readonly base: string; readonly suffix: string; readonly fitted: string }[] = [
  { base: "Legs & Core", suffix: " (copy)", fitted: "Legs & Core (copy)" },
  { base: "Push Skill · Pull Strength B", suffix: " (copy)", fitted: "Push Skill · Pull St… (copy)" },
  { base: "Push Skill · Pull Strength B", suffix: " (copy 12)", fitted: "Push Skill · Pull… (copy 12)" },
  { base: "Push Skill · Pull St…", suffix: " (copy)", fitted: "Push Skill · Pull St… (copy)" },
  { base: "Push Skill · Pull St…", suffix: " (copy 2)", fitted: "Push Skill · Pull… (copy 2)" },
  { base: "ABCDEFGHIJKLMNOPQRSTU", suffix: " (copy)", fitted: "ABCDEFGHIJKLMNOPQRSTU (copy)" },
  { base: "ABCDEFGHIJKLMNOPQRSTUV", suffix: " (copy)", fitted: "ABCDEFGHIJKLMNOPQRST… (copy)" },
  { base: "Pull Skill · Push Strength A — deload week", suffix: " (copy 3)", fitted: "Pull Skill · Push… (copy 3)" },
  { base: "Άνω σώμα · Δύναμη και τεχνική Β", suffix: " (copy)", fitted: "Άνω σώμα · Δύναμη κα… (copy)" },
  { base: "", suffix: " (copy)", fitted: " (copy)" },
];

/** Swift's own answers on the probes — `workoutNameCap.test.ts` holds the functions above to them. */
export const SWIFT_PROBES: readonly { readonly name: string; readonly length: number; readonly clamped: string }[] = [
  { name: "", length: 0, clamped: "" },
  { name: "Legs & Core", length: 11, clamped: "Legs & Core" },
  { name: "Push Skill · Pull Strength B", length: 28, clamped: "Push Skill · Pull Strength B" },
  { name: "Push Skill · Pull Strength B!", length: 29, clamped: "Push Skill · Pull Strength B" },
  { name: "Pull Skill · Push Strength A — deload week", length: 42, clamped: "Pull Skill · Push Strength A" },
  { name: "Mobility & Handstand Play", length: 25, clamped: "Mobility & Handstand Play" },
  { name: "Άνω σώμα · Δύναμη και τεχνική Β", length: 31, clamped: "Άνω σώμα · Δύναμη και τεχνικ" },
  { name: "ABCDEFGHIJKLMNOPQRSTUVWXYZ12", length: 28, clamped: "ABCDEFGHIJKLMNOPQRSTUVWXYZ12" },
  { name: "ABCDEFGHIJKLMNOPQRSTUVWXYZ123", length: 29, clamped: "ABCDEFGHIJKLMNOPQRSTUVWXYZ12" },
  { name: "Café crème · Tempo squats 3×5 @ 70%", length: 35, clamped: "Café crème · Tempo squats 3×" },
];

/* ── An exercise's name (his 2026-10-09 word): the SAME cap, `ExerciseNameCap` in the same Swift file. ── */

export const EXERCISE_NAME_MAX = 28;
export const EXERCISE_NAME_COUNTER_FROM = 24;

/** What the field keeps when `old` is edited into `typed`: up to the cap as typed; a short name stops at the cap (a
 *  paste keeps its first characters); a name ALREADY over it may shrink but never grow — it is never cut. */
export function acceptExerciseName(old: string, typed: string): string {
  const n = [...typed].length;
  if (n <= EXERCISE_NAME_MAX) return typed;
  const was = [...old].length;
  if (was > EXERCISE_NAME_MAX) return n <= was ? typed : old;
  return [...typed].slice(0, EXERCISE_NAME_MAX).join("");
}

/** The quiet counter (`24/28`) from `EXERCISE_NAME_COUNTER_FROM` on; null below it. */
export function exerciseNameCounter(name: string): string | null {
  const n = [...name].length;
  return n >= EXERCISE_NAME_COUNTER_FROM ? `${n}/${EXERCISE_NAME_MAX}` : null;
}

/** Swift's own answers — `workoutNameCap.test.ts` holds `acceptExerciseName` and `exerciseNameCounter` to them. */
export const SWIFT_EXERCISE_PROBES: readonly { readonly old: string; readonly typed: string; readonly kept: string; readonly counter: string | null }[] = [
  { old: "Ring Row", typed: "Ring Rows", kept: "Ring Rows", counter: null },
  { old: "Ring Support Hold Turned Ou", typed: "Ring Support Hold Turned Out", kept: "Ring Support Hold Turned Out", counter: "28/28" },
  { old: "Ring Support Hold Turned Out", typed: "Ring Support Hold Turned Out!", kept: "Ring Support Hold Turned Out", counter: "28/28" },
  { old: "", typed: "Assisted Full Front Lever (Pulley) on rings", kept: "Assisted Full Front Lever (P", counter: "28/28" },
  { old: "Pull-Up", typed: "", kept: "", counter: null },
  { old: "Prone Incline Y-Raise (Trap-3) — plates", typed: "Prone Incline Y-Raise (Trap-3) — plate", kept: "Prone Incline Y-Raise (Trap-3) — plate", counter: "38/28" },
  { old: "Prone Incline Y-Raise (Trap-3) — plates", typed: "Prone Incline Y-Raise (Trap-3) — platez", kept: "Prone Incline Y-Raise (Trap-3) — platez", counter: "39/28" },
  { old: "Prone Incline Y-Raise (Trap-3) — plates", typed: "Prone Incline Y-Raise (Trap-3) — plates!", kept: "Prone Incline Y-Raise (Trap-3) — plates", counter: "39/28" },
  { old: "Άνω σώμα · Δύναμη και τεχνικ", typed: "Άνω σώμα · Δύναμη και τεχνική", kept: "Άνω σώμα · Δύναμη και τεχνικ", counter: "28/28" },
];
